<template>
  <ProfileView v-if="view === 'profile'" @select="onSelectProfile" />
  <LoadingView v-else-if="view === 'loading'" @complete="onLoadingComplete" />
  <template v-else>
    <Desktop />
    <Teleport to="body">
      <div
        v-if="showMobileNotice"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 px-4 py-6 font-pixel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-notice-title"
        aria-describedby="mobile-notice-desc"
      >
        <div
          class="border-2 border-black bg-white shadow-[2px_2px_0_0_#000] w-full max-w-md p-6 sm:p-8 text-black"
        >
          <h2 id="mobile-notice-title" class="text-2xl sm:text-3xl font-bold mb-3 text-center">
            Desktop recommended
          </h2>
          <p id="mobile-notice-desc" class="text-lg sm:text-xl leading-snug opacity-95 text-center mb-6">
            This website is optimized for laptop and desktop viewing for the best experience. You can still
            continue using your current device.
          </p>
          <button
            type="button"
            class="w-full border-2 border-black bg-gray-100 px-4 py-3 text-xl font-medium text-black transition-colors hover:bg-gray-200 active:bg-gray-300"
            @click="dismissMobileNotice"
          >
            Continue
          </button>
        </div>
      </div>
    </Teleport>
  </template>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import Desktop from './components/Desktop.vue'
import LoadingView from './components/LoadingView.vue'
import ProfileView from './components/ProfileView.vue'
import quadraStartup from '@/assets/mac_quadra_startup.mp3'

const FIRST_LAUNCH_KEY = 'retro_portfolio_profile_completed'
const MOBILE_NOTICE_KEY = 'buildwithsaad_mobile_desktop_notice_dismissed'
const MOBILE_MEDIA = '(max-width: 900px)'

const showMobileNotice = ref(false)

function isSmallScreen() {
  if (typeof window === 'undefined') return false
  return window.matchMedia(MOBILE_MEDIA).matches
}

function updateMobileNotice() {
  if (view.value !== 'desktop') {
    showMobileNotice.value = false
    return
  }
  try {
    const dismissed = window.sessionStorage.getItem(MOBILE_NOTICE_KEY) === '1'
    showMobileNotice.value = isSmallScreen() && !dismissed
  } catch {
    showMobileNotice.value = isSmallScreen()
  }
}

function dismissMobileNotice() {
  try {
    window.sessionStorage.setItem(MOBILE_NOTICE_KEY, '1')
  } catch {
    // Ignore storage failures (private mode, etc.).
  }
  showMobileNotice.value = false
}

let mqSmall = null

function onViewportOrMqChange() {
  updateMobileNotice()
}

function getInitialView() {
  try {
    // If the user already launched once, skip profile/loading on refresh.
    // sessionStorage is per-tab, so a new tab counts as a fresh start.
    return window.sessionStorage.getItem(FIRST_LAUNCH_KEY) ? 'desktop' : 'profile'
  } catch {
    return 'profile'
  }
}

const view = ref(getInitialView())
let hasPlayedStartup = false

watch(
  view,
  () => {
    updateMobileNotice()
  },
  { flush: 'post' },
)

function onSelectProfile() {
  if (hasPlayedStartup) return
  hasPlayedStartup = true

  view.value = 'loading'

  // Play the startup sound after the 2s loading view.
  // Chrome often blocks audible autoplay, but muted playback triggered by user interaction is allowed.
  const startupAudio = new Audio(quadraStartup)
  startupAudio.preload = 'auto'
  startupAudio.loop = false
  startupAudio.volume = 1
  startupAudio.currentTime = 0

  try {
    startupAudio.muted = true
    // Start muted immediately (allowed due to user click), then unmute after loading completes.
    startupAudio.play().catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('Muted startup sound play failed.', err)
    })
  } catch {
    // eslint-disable-next-line no-console
    console.warn('Startup sound muted play failed.')
  }

  window.setTimeout(() => {
    try {
      // Restart from the beginning so the audible sound lines up with the desktop appearing.
      startupAudio.currentTime = 0
      startupAudio.muted = false
      startupAudio.play().catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('Unmuted startup sound play failed.', err)
      })
    } catch {
      // eslint-disable-next-line no-console
      console.warn('Startup sound unmute failed.')
    }
  }, 2000)
}

function onLoadingComplete() {
  view.value = 'desktop'
  try {
    window.sessionStorage.setItem(FIRST_LAUNCH_KEY, '1')
  } catch {
    // Ignore localStorage failures (private mode, blocked storage, etc).
  }
}

onMounted(() => {
  if (typeof window === 'undefined') return
  mqSmall = window.matchMedia(MOBILE_MEDIA)
  mqSmall.addEventListener('change', onViewportOrMqChange)
  window.addEventListener('resize', onViewportOrMqChange)
  updateMobileNotice()
})

onUnmounted(() => {
  if (typeof window === 'undefined') return
  if (mqSmall) mqSmall.removeEventListener('change', onViewportOrMqChange)
  window.removeEventListener('resize', onViewportOrMqChange)
})
</script>

<style>
#app {
  font-family: 'VT323', monospace;
  -webkit-font-smoothing: none;
  -moz-osx-font-smoothing: unset;
}

* {
  border-radius: 0 !important;
}
</style>
